import { TextEllipsis } from '@app/components/various/TextEllipsis';
import { Tooltip } from '@app/components/various/Tooltip';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { Tabs } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  tabParameters: Tabs.Tab;
  isSelected: boolean;
  tablistName: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isFluid?: boolean;
  className?: string;
}

export const Tab = ({ tabParameters, isSelected, tablistName, onChange, isFluid, className }: Props) => {
  const { t } = useTranslation(['common']);

  const { label, id, extraInfo, icon: Icon, iconTooltip, alertIcon: AlertIcon, alertTooltip, testId } = tabParameters;

  const hasAnyTooltip = isDefined(iconTooltip) || isDefined(alertTooltip);

  const htmlId = `${tablistName}-${id}`;

  const textContent = (
    <>
      {label}
      {isDefined(extraInfo) && <span> ({extraInfo})</span>}
    </>
  );

  return (
    <div
      aria-selected={isSelected}
      className={cn(
        'relative -mb-[1px] flex content-center overflow-hidden rounded-t-regular px-8 py-2 text-neutral-60 transition-colors body-1 hover:bg-neutral-96 hover:text-neutral-40 has-[input:focus-visible]:z-elevated has-[input:focus-visible]:rounded-regular has-[input:focus-visible]:focus-outline',
        'after:absolute after:-bottom-[1px] after:left-0 after:h-0.5 after:w-full after:transition-colors after:content-empty hover:after:bg-neutral-60',
        {
          'text-neutral-20 after:h-[3px] after:bg-neutral-20 hover:text-neutral-20 hover:after:bg-neutral-20': isSelected,
        },
        { 'w-full px-2': isFluid },
        className,
      )}
      data-testid={testId}
    >
      <input id={htmlId} name={tablistName} value={id} checked={isSelected} type="radio" className="sr-only" onChange={onChange} />
      <label className="mx-auto flex gap-2 overflow-hidden absolute-cursor-pointer" htmlFor={htmlId}>
        {isDefined(Icon) && (
          <Tooltip content={iconTooltip}>
            <div className="z-[2] cursor-pointer">
              <Icon className="size-5" />
            </div>
          </Tooltip>
        )}
        <TextEllipsis>{textContent}</TextEllipsis>
        {isDefined(AlertIcon) && (
          <Tooltip content={alertTooltip}>
            <div className="z-[2] cursor-pointer">
              <AlertIcon className="size-5 text-warning-48" />
            </div>
          </Tooltip>
        )}
      </label>
      {hasAnyTooltip && (
        <span className="sr-only">
          {iconTooltip}{' '}
          {isDefined(alertTooltip) && (
            <strong>
              {t('common:warning')} {alertTooltip}
            </strong>
          )}
        </span>
      )}
    </div>
  );
};
